/**
 * Selector de cliente para la creación de una reparación nueva.
 * Permite filtrar clientes existentes o desplegar un mini-form para crear uno
 * en línea. Extraído de `RepairForm.jsx`.
 */
import { useMemo, useState } from "react";
import { Search, UserPlus } from "lucide-react";
import { matchesAllTokens } from "@/lib/searchNormalize";

export default function CustomerPicker({
  customers, onSelect, showNewCustomer, setShowNewCustomer,
  newCustomer, setNewCustomer, onCreate,
}) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!q.trim()) return customers.slice(0, 8);
    // Iter 128 — Búsqueda por tokens (AND, orden libre) insensible a
    // acentos/mayúsculas sobre los campos consultables del cliente.
    return customers
      .filter((c) =>
        matchesAllTokens(
          [c.name, c.billing_name, c.code, c.tax_id, c.billing_tax_id, c.phone, c.email],
          q,
        )
      )
      .slice(0, 10);
  }, [q, customers]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Cliente *
        </label>
        <button
          type="button"
          onClick={() => setShowNewCustomer(!showNewCustomer)}
          data-testid="repair-inline-newcustomer"
          className="text-[10px] font-mono uppercase tracking-wider text-[#6E8B3D] hover:underline inline-flex items-center gap-1"
        >
          <UserPlus size={11} /> {showNewCustomer ? "Cancelar" : "Nuevo cliente"}
        </button>
      </div>

      {showNewCustomer ? (
        <div className="border border-[#6E8B3D] p-3 bg-[#F0F4E8] space-y-2">
          <input
            required
            placeholder="Nombre *"
            value={newCustomer.name}
            onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
            data-testid="repair-inline-customer-name"
            className="w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
          />
          <input
            placeholder="NIF / DNI"
            value={newCustomer.tax_id}
            onChange={(e) => setNewCustomer({ ...newCustomer, tax_id: e.target.value })}
            className="w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
          />
          <input
            placeholder="Teléfono"
            value={newCustomer.phone}
            onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
            className="w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
          />
          <input
            type="text"
            placeholder="Email (varios separados por , o ;)"
            value={newCustomer.email}
            onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
            className="w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
          />
          <button
            type="button"
            onClick={onCreate}
            disabled={!newCustomer.name}
            data-testid="repair-inline-customer-save"
            className="w-full bg-[#111110] text-white hover:bg-[#6E8B3D] px-3 py-2 text-xs font-semibold disabled:opacity-50"
          >
            Guardar cliente
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#525252]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, código, NIF, teléfono..."
              data-testid="repair-customer-search"
              className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none pl-8 pr-3 py-2 text-sm rounded-none"
            />
          </div>
          <div className="border border-[#E5E5E0] max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-[#525252] text-center">
                Sin resultados. Crea uno nuevo arriba.
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onSelect(c)}
                  data-testid={`repair-customer-pick-${c.id}`}
                  className="w-full text-left px-3 py-2 border-b border-[#E5E5E0] last:border-0 hover:bg-[#F0F4E8] transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{c.name}</div>
                      <div className="text-[10px] font-mono text-[#525252]">
                        {c.code} {c.tax_id && `· ${c.tax_id}`} {c.phone && `· ${c.phone}`}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          {customers.length > 8 && !q && (
            <div className="text-[10px] text-[#525252]">
              Escribe para buscar entre los {customers.length} clientes.
            </div>
          )}
        </>
      )}
    </div>
  );
}
